import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';

interface Article {
  articleId: string;
  title: string;
  body: string;
  tags: string[];
  active: boolean;
  updatedOn?: string;
}

type Draft = { articleId?: string; title: string; body: string; tags: string; active: boolean };
const emptyDraft = (): Draft => ({ title: '', body: '', tags: '', active: true });

/** A group of questions the assistant could not answer from the knowledge base. */
interface GapCluster {
  clusterId: string;
  label: string;
  questions: string[];
  count: number;
  escalated: number;
  unmatched: number;
  relatedTitles: string[];
  accounts: number;
  newestOn: string;
  oldestOn: string;
}

interface GeneratedDraft {
  mode: 'new' | 'update';
  articleId?: string;
  title: string;
  body: string;
  tags: string[];
  rationale: string;
  /** Specifics in the draft traceable to nothing the model was given. Checked in code, not self-reported. */
  inventedFacts: string[];
}

const when = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '');

export function KnowledgeBase() {
  const isAdmin = useIsSiteAdmin();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openGap, setOpenGap] = useState<string | null>(null);
  /** Carried alongside the editor so the agent can still see what the draft was written FROM. */
  const [generated, setGenerated] = useState<GeneratedDraft | null>(null);
  const [gapErr, setGapErr] = useState<string | null>(null);

  const articles = useQuery({
    queryKey: ['kb-articles'],
    queryFn: () => api.get<Article[]>('/admin/support/kb'),
    enabled: isAdmin,
  });

  const gaps = useQuery({
    queryKey: ['kb-gaps'],
    queryFn: () => api.get<{ clusters: GapCluster[]; retentionDays: number }>('/admin/support/kb/gaps'),
    enabled: isAdmin,
  });

  /**
   * Write a draft for one group of questions.
   *
   * It saves nothing — the draft lands in the ordinary editor below, where it is edited and saved like any
   * other article. It arrives INACTIVE whatever the model suggested: the assistant retrieves active
   * articles only, so nothing written here can reach a customer until a person has read it and ticked the
   * box.
   */
  const generate = useMutation({
    mutationFn: (clusterId: string) =>
      api.post<{ draft: GeneratedDraft }>('/admin/support/kb/draft', { clusterId }),
    onSuccess: (r) => {
      setGapErr(null);
      setErr(null);
      setGenerated(r.draft);
      setDraft({
        ...(r.draft.mode === 'update' && r.draft.articleId ? { articleId: r.draft.articleId } : {}),
        title: r.draft.title,
        body: r.draft.body,
        tags: r.draft.tags.join(', '),
        active: false,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onError: (e) => setGapErr(e instanceof ApiError ? e.message : 'Could not write a draft'),
  });

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const payload = { title: d.title.trim(), body: d.body.trim(), tags: d.tags.split(',').map((t) => t.trim()).filter(Boolean), active: d.active };
      return d.articleId
        ? api.patch(`/admin/support/kb/${d.articleId}`, payload)
        : api.post('/admin/support/kb', payload);
    },
    onSuccess: () => {
      setDraft(null); setErr(null); setGenerated(null);
      qc.invalidateQueries({ queryKey: ['kb-articles'] });
      // The gap list is unchanged by saving, but the article that answers it now exists — refetch so the
      // related-article column reflects it on the next retrieval.
      qc.invalidateQueries({ queryKey: ['kb-gaps'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save the article'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/support/kb/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kb-articles'] }),
  });

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>This area is for Lakota staff.</p></div>;
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Knowledge base</h1>
          <div className="sub">Articles that ground the support assistant. Keep them short, specific, and current.</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setErr(null); setGenerated(null); setDraft(emptyDraft()); }}>+ New article</button>
      </div>

      {draft && (
        <div className="panel" style={{ marginBottom: 20 }}>
          <h3 className="review-h">
            {generated ? (generated.mode === 'update' ? 'Suggested update' : 'Suggested article')
              : draft.articleId ? 'Edit article' : 'New article'}
          </h3>
          {generated && (
            <div className="kb-generated">
              <p className="kb-generated-why">
                <strong>Drafted from customer questions.</strong>{' '}
                {generated.rationale || 'Read it before saving — it was written from support conversations, not from the product.'}
              </p>
              {generated.mode === 'update' && (
                <p className="kb-generated-why">This is proposed as an <strong>edit to an existing article</strong>, not a second one on the same subject.</p>
              )}
              {generated.inventedFacts.length > 0 && (
                /*
                  * The check that matters. Every number, version, price, link and address here appears in
                  * NOTHING the model was given — so each one is either something it knew from elsewhere or
                  * something it made up, and from the outside those look identical. An article is repeated
                  * to every customer who asks, so a wrong specific here is not one wrong answer.
                  */
                <p className="kb-generated-warn">
                  <strong>Check these before saving — they came from nowhere in the source questions:</strong>{' '}
                  {generated.inventedFacts.map((f) => <code key={f} className="kb-fact">{f}</code>)}
                </p>
              )}
              <p className="kb-generated-why kb-generated-dim">
                Saved inactive. The assistant only uses active articles, so nothing here reaches a customer
                until you tick the box below.
              </p>
            </div>
          )}
          <label className="field"><span className="field-label">Title</span>
            <input className="input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="e.g. Device Service not detected" autoFocus /></label>
          <label className="field"><span className="field-label">Tags <span className="opt">(comma-separated)</span></span>
            <input className="input" value={draft.tags} onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
              placeholder="device service, scanner, connection" /></label>
          <label className="field"><span className="field-label">Body</span>
            <textarea className="input" style={{ minHeight: 200, fontFamily: 'inherit' }} value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder="Plain text or light markdown. Explain the answer clearly and concisely." /></label>
          <label className="checkbox-row" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            <span>Active (used by the assistant)</span>
          </label>
          {err && <p className="cart-error" style={{ marginTop: 10 }}>{err}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary btn-sm" disabled={save.isPending || draft.title.trim().length < 3 || draft.body.trim().length < 10}
              onClick={() => save.mutate(draft)}>{save.isPending ? 'Saving…' : 'Save article'}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setDraft(null); setErr(null); setGenerated(null); }}>Cancel</button>
          </div>
        </div>
      )}

      <GapsPanel
        gaps={gaps.data?.clusters ?? []}
        loading={gaps.isLoading}
        retentionDays={gaps.data?.retentionDays ?? 0}
        open={openGap}
        setOpen={setOpenGap}
        onDraft={(id) => generate.mutate(id)}
        drafting={generate.isPending ? (generate.variables ?? null) : null}
        error={gapErr}
      />

      <div className="panel" style={{ padding: 0 }}>
        {articles.isLoading ? (
          <div className="loading-screen">// loading articles…</div>
        ) : (articles.data?.length ?? 0) === 0 ? (
          <p className="sub" style={{ padding: 16 }}>No articles yet. Add your first so the assistant can answer from it.</p>
        ) : (
          <ul className="kb-list">
            {articles.data?.map((a) => (
              <li key={a.articleId} className="kb-row">
                <div className="kb-row-info">
                  <span className="kb-title">{a.title}{!a.active && <span className="opt"> · inactive</span>}</span>
                  {a.tags.length > 0 && <span className="kb-tags">{a.tags.join(' · ')}</span>}
                </div>
                <div className="kb-row-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => { setErr(null); setGenerated(null); setDraft({ articleId: a.articleId, title: a.title, body: a.body, tags: a.tags.join(', '), active: a.active }); }}>Edit</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { if (confirm(`Delete "${a.title}"?`)) remove.mutate(a.articleId); }}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * What the knowledge base could not answer.
 *
 * Ranked by DISTINCT customers, not by volume: one person asking eleven times is one confused customer to
 * help, eleven customers asking once is a missing article. Sitting above the article list because it is the
 * reason to write one — a knowledge base maintained from memory covers what its author already thought of.
 */
function GapsPanel({ gaps, loading, retentionDays, open, setOpen, onDraft, drafting, error }: {
  gaps: GapCluster[];
  loading: boolean;
  retentionDays: number;
  open: string | null;
  setOpen: (id: string | null) => void;
  onDraft: (clusterId: string) => void;
  /** The cluster currently being drafted, so only its button says so. */
  drafting: string | null;
  error: string | null;
}) {
  if (loading) return <div className="panel" style={{ marginBottom: 20 }}><div className="loading-screen">// reading support conversations…</div></div>;

  return (
    <div className="panel kb-gaps" style={{ marginBottom: 20 }}>
      <h3 className="review-h">Questions the assistant couldn&rsquo;t answer</h3>
      <p className="sub" style={{ marginTop: 0 }}>
        Recorded when the knowledge base matched nothing, or when the conversation went to a human anyway.
        {retentionDays > 0 && ` Kept for ${retentionDays} days.`}
      </p>

      {error && <p className="cart-error">{error}</p>}

      {gaps.length === 0 ? (
        <p className="sub">
          Nothing recorded yet — either the articles are answering people, or no one has asked since this
          started being tracked.
        </p>
      ) : (
        <ul className="kb-gap-list">
          {gaps.map((g) => (
            <li key={g.clusterId} className="kb-gap">
              <div className="kb-gap-head">
                <button className="kb-gap-label" onClick={() => setOpen(open === g.clusterId ? null : g.clusterId)}>
                  {g.label}
                </button>
                <div className="kb-gap-meta">
                  {/* Customers first — it is what decides whether this is a gap or one person's bad day. */}
                  <span className="kb-gap-stat"><strong>{g.accounts}</strong> customer{g.accounts === 1 ? '' : 's'}</span>
                  <span className="kb-gap-stat">{g.count} question{g.count === 1 ? '' : 's'}</span>
                  {g.escalated > 0 && <span className="kb-gap-stat kb-gap-esc">{g.escalated} escalated</span>}
                  {g.unmatched > 0 && <span className="kb-gap-stat">{g.unmatched} with no article at all</span>}
                </div>
                <button className="btn btn-ghost btn-sm" disabled={drafting !== null}
                  onClick={() => onDraft(g.clusterId)}>
                  {drafting === g.clusterId ? 'Writing…' : 'Draft an article'}
                </button>
              </div>
              {g.relatedTitles.length > 0 && (
                <div className="kb-gap-related">
                  Closest existing: {g.relatedTitles.join(' · ')}
                </div>
              )}
              {open === g.clusterId && (
                <div className="kb-gap-questions">
                  <div className="kb-gap-span">{when(g.oldestOn)} – {when(g.newestOn)}</div>
                  {/* The customers' own words. Read them before accepting anything written from them. */}
                  <ul>{g.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
